import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Modal from "sushiweb/Modal";
import Button from "sushiweb/Button/Button";

import Text from "../../shared/text/Text";
import BODY_TYPE_REGISTRY from "./bodyRegistry";
import { handleActionType } from "../../../utils/helpers";
import { setErrorToast } from "../../../redux/dispatchers/userDispatcher";
import { closeModal } from "../../../redux/dispatchers/modalDispatcher";
import { getClaimOwnershipDeviceId } from "../../../utils/claimOwnershipDeviceId";
import { collectErrors, isBodyValid } from "./validators";
import { isBodyReady } from "./utils";
import styles from "./ClaimOwnershipModal.module.scss";
import ZIcon from "../../shared/ZIcon/ZIcon";
import { isEmpty } from "lodash-es";
import { HEADER_KEYS } from "../../../utils/constants/others";

const BUTTON_APPEARANCE_BY_VARIANT = {
    filled: "solid",
    solid: "solid",
    bordered: "outline",
    outline: "outline",
    ghost: "link",
    text: "link",
    link: "link",
};

const SUSHI_BTN_COLORS = new Set([
    "red",
    "green",
    "blue",
    "yellow",
    "purple",
    "lime",
    "coal",
    "chalk",
]);

const indexErrorsByField = (errors = []) =>
    errors.reduce((acc, item) => {
        if (item?.field && item?.error) acc[item.field] = item.error;
        return acc;
    }, {});

const ButtonContentWithLoader = ({ loading, children }) => (
    <span className={styles.btnContent}>
        <span
            className={`${styles.btnLabel} ${loading ? styles.btnLabelHidden : ""}`}
            aria-hidden={loading}
        >
            {children}
        </span>
        <span
            className={`${styles.btnSpinnerWrap} ${loading ? styles.btnSpinnerWrapVisible : ""}`}
            aria-hidden={!loading}
        >
            <span
                className={styles.btnSpinner}
                role="status"
                aria-label="Loading"
            />
        </span>
    </span>
);

const renderHeader = ({ header, onClose, bodyType }) => {
    if (isEmpty(header)) return null;

    const { imageUrl, title, showCloseBtn } = header ?? {};
    if (!imageUrl && !title && !showCloseBtn) return null;
    const imageSizeClass =
        bodyType === "SUCCESS"
            ? styles.headerImageFull
            : styles.headerImageCompact;
    const isCloseOnly = showCloseBtn && !imageUrl && !title;

    return (
        <div
            className={`${styles.header} ${header.imageUrl ? styles.headerWithImage : ""} ${header.imageUrl ? imageSizeClass : ""} ${showCloseBtn && header.imageUrl ? styles.headerWithClose : ""} ${isCloseOnly ? styles.headerCloseOnly : ""}`}
        >
            {imageUrl ? <img src={imageUrl} alt="" /> : null}
            {title && <Text uiData={title} className={styles.headerTitle} />}
            {showCloseBtn && (
                <ZIcon
                    unicode="E921"
                    className={styles.headerIcon}
                    onClick={onClose}
                />
            )}
        </div>
    );
};

const ClaimOwnershipModal = ({ visible, onClose, data }) => {
    const [modalData, setModalData] = useState(data);
    const [bodyState, setBodyState] = useState({});
    const [errorPatch, setErrorPatch] = useState(null);
    const [loadingIdx, setLoadingIdx] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        setModalData(data);
        setBodyState({});
        setErrorPatch(null);
        setLoadingIdx(null);
    }, [data]);

    const applyAction = useCallback((resp) => {
        const action = resp?.action;
        if (!action) return;

        if (action.type === "UPDATE_MODAL") {
            setModalData(action.data);
            setBodyState({});
            setErrorPatch(null);
            setLoadingIdx(null);
        } else if (action.type === "MODAL_ERROR") {
            setErrorPatch(action.data ?? null);
        }
    }, []);

    const setBodyStateWithErrorClear = useCallback((updater) => {
        setBodyState(updater);
        setErrorPatch(null);
    }, []);

    const patchedErrors = indexErrorsByField(errorPatch?.errors);
    const allErrors = {
        ...collectErrors(modalData?.body, bodyState),
        ...patchedErrors,
    };
    const patchedDisabledFields = new Set(errorPatch?.disabledFields ?? []);

    const isFooterDisabled =
        !isBodyValid(modalData?.body, bodyState) ||
        !isBodyReady(modalData?.body, bodyState) ||
        Object.keys(patchedErrors).length > 0 ||
        patchedDisabledFields.size > 0;

    const dismissModal = useCallback(
        () => dispatch(closeModal(modalData?.modalConfig?.id)),
        [dispatch, modalData],
    );

    const buildClaimOwnershipHeaders = () => {
        const xAppDeviceId = getClaimOwnershipDeviceId();
        return xAppDeviceId
            ? { [HEADER_KEYS.X_APP_DEVICE_ID_HEADER]: xAppDeviceId }
            : undefined;
    };

    const dispatchAction = useCallback(
        (action) => {
            if (!action) return;
            const { type, data: actionData } = action;

            handleActionType(type, actionData, undefined, {
                params: bodyState,
                body: bodyState,
                headers: buildClaimOwnershipHeaders(),
                SuccessCallback: applyAction,
                dismissModal,
            });
        },
        [applyAction, bodyState, dismissModal],
    );

    const handleButtonClick = useCallback(
        (action, idx) => {
            if (!action || isFooterDisabled || loadingIdx !== null) return;
            const isEndpoint = action?.type === "ENDPOINT";
            if (isEndpoint) setLoadingIdx(idx);
            handleActionType(action.type, action.data, undefined, {
                params: bodyState,
                body: bodyState,
                headers: buildClaimOwnershipHeaders(),
                SuccessCallback: (resp) => {
                    if (isEndpoint) setLoadingIdx(null);
                    applyAction(resp);
                },
                ErrCallback: (err) => {
                    if (isEndpoint) setLoadingIdx(null);
                    const message =
                        err?.response?.data?.error?.message ||
                        "Something went wrong. Please try again.";
                    dispatch(setErrorToast(message));
                },
                dismissModal,
            });
        },
        [
            applyAction,
            bodyState,
            dispatch,
            isFooterDisabled,
            loadingIdx,
            dismissModal,
        ],
    );

    const renderBody = (body) => {
        const { type, imageUrl } = body ?? {};

        const Component = BODY_TYPE_REGISTRY[type];
        if (!Component) return null;
        return (
            <div
                className={`${styles.body} ${imageUrl ? (type === "SUCCESS" ? styles.bodyWithImage2 : styles.bodyWithImage) : ""}`}
            >
                <Component
                    body={body}
                    state={bodyState}
                    setState={setBodyStateWithErrorClear}
                    dispatchAction={dispatchAction}
                    errors={allErrors}
                    disabledFields={patchedDisabledFields}
                />
            </div>
        );
    };

    const renderFooter = (footer) => {
        if (!footer?.buttonConfig?.length) return null;
        return (
            <div className={styles.footer}>
                {footer.buttonConfig.map((btn, idx) => {
                    const { mainContent, action, variant, theme } = btn ?? {};
                    const isLoading = loadingIdx === idx;
                    const appearance =
                        BUTTON_APPEARANCE_BY_VARIANT[variant] ?? "solid";
                    const isTextBtn = appearance === "link";
                    return (
                        <Button
                            key={idx}
                            btnColor={
                                SUSHI_BTN_COLORS.has(theme) ? theme : "red"
                            }
                            appearance={appearance}
                            fullWidth
                            borderRadius="12px"
                            disabled={isFooterDisabled || loadingIdx !== null}
                            onClick={() => handleButtonClick(action, idx)}
                            className={`${styles.footerBtn} ${isTextBtn ? styles.footerBtnText : ""} ${
                                isFooterDisabled && !isTextBtn
                                    ? styles.disabled
                                    : ""
                            }`}
                        >
                            <ButtonContentWithLoader loading={isLoading}>
                                <Text uiData={mainContent?.title} />
                            </ButtonContentWithLoader>
                        </Button>
                    );
                })}
            </div>
        );
    };

    const renderBanner = (banner) => {
        if (!banner?.title) return null;
        const variantClass =
            banner.variant === "error"
                ? styles.bannerError
                : banner.variant === "info"
                  ? styles.bannerInfo
                  : styles.bannerWarning;
        return (
            <div className={`${styles.banner} ${variantClass}`}>
                {banner.icon ? (
                    <ZIcon
                        unicode={banner.icon}
                        className={styles.bannerIcon}
                    />
                ) : null}
                <Text uiData={banner.title} className={styles.bannerText} />
            </div>
        );
    };

    const { modalConfig, header, body, footer } = modalData ?? {};
    const banner = errorPatch?.banner ?? modalData?.banner;
    const { closeOnOutsideClick = true } = modalConfig ?? {};

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            overlayClickClose={closeOnOutsideClick}
            isCentered
            borderRadius="24px"
            type="midScreenSize"
            className={styles.modalRoot}
        >
            {modalData && (
                <div
                    className={`${styles.container} ${
                        header?.imageUrl ? styles.containerHero : ""
                    }`}
                >
                    {renderHeader({ header, onClose, bodyType: body?.type })}
                    {renderBody(body)}
                    <div>
                        {renderBanner(banner)}
                        {renderFooter(footer)}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ClaimOwnershipModal;
